import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { Loader2, AlertCircle, Download, Music, Video, Clock } from 'lucide-react';
import { getClipByShareToken } from '../../services/clipsService';
import { useWhiteLabel } from '../../context/WhiteLabelContext';

const LAYOUT_LABELS = {
  gallery_view: 'Gallery',
  focus: 'Focus',
  speaker_top: 'Speaker Top',
  speaker_side: 'Speaker Side',
  audio_only: 'Audio',
};

function formatDuration(ms) {
  if (!ms || ms < 0) return '0:00';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function ClipPreview() {
  const { shareToken } = useParams();
  const { branding } = useWhiteLabel();
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchClip = async () => {
    try {
      // httpServices auto-unwraps response.data — `res` is already the body.
      const res = await getClipByShareToken(shareToken);
      const data = res?.clip;
      if (!data) {
        setError('Clip not found');
      } else {
        setClip(data);
        setError(null);
      }
    } catch (err) {
      console.error('[ClipPreview] fetch failed:', err);
      const msg = err?.response?.data?.message || 'Could not load clip';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shareToken) return;
    setLoading(true);
    fetchClip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  // Poll while still processing
  useEffect(() => {
    const inFlight =
      clip && (clip.processingStatus === 'pending' || clip.processingStatus === 'processing');
    if (!inFlight) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(fetchClip, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip?.processingStatus]);

  const isAudio = clip?.layout === 'audio_only' || clip?.format === 'mp3';
  const Icon = isAudio ? Music : Video;
  const logoUrl = branding?.logoUrl || branding?.logo || null;
  const productName = branding?.productName || branding?.name || 'R-Link';

  return (
    <div className="min-h-screen bg-[#001233] text-white">
      <header className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt={productName} className="h-7 w-auto" />
        ) : (
          <div className="text-lg font-semibold bg-gradient-to-r from-[#6a1fbf] to-[#00c853] bg-clip-text text-transparent">
            {productName}
          </div>
        )}
        <span className="text-xs text-white/40 ml-2">Clip</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/50">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white/5 border border-red-500/30 rounded-2xl p-8 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">Clip unavailable</h2>
            <p className="text-sm text-white/60">{error}</p>
          </div>
        ) : !clip ? null : clip.processingStatus !== 'completed' || !clip.fileUrl ? (
          <div className="bg-white/5 border border-amber-500/20 rounded-2xl p-10 text-center">
            <Loader2 className="w-10 h-10 text-amber-400 mx-auto mb-3 animate-spin" />
            <h2 className="text-lg font-semibold mb-1">Clip is still processing</h2>
            <p className="text-sm text-white/60">
              {clip.processingStatus === 'failed'
                ? 'Processing failed. Ask the owner to recreate the clip.'
                : 'This page will refresh automatically when the clip is ready.'}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start gap-3 mb-5">
              <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#6a1fbf]/15 border border-[#6a1fbf]/30 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#a677d9]" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-semibold text-white truncate">{clip.name || 'Untitled clip'}</h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/50 mt-1">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatDuration(clip.totalDurationMs)}
                  </span>
                  <span>{LAYOUT_LABELS[clip.layout] || clip.layout}</span>
                  {clip.segments?.length > 1 && <span>{clip.segments.length} segments</span>}
                  {clip.fileSize > 0 && <span>{formatBytes(clip.fileSize)}</span>}
                </div>
              </div>
              {clip.shareSettings?.allowDownload !== false && (
                <a
                  href={clip.fileUrl}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-medium"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
              )}
            </div>

            {/* Player */}
            <div className={`bg-black rounded-2xl overflow-hidden ${isAudio ? 'p-6' : ''}`}>
              {isAudio ? (
                <audio src={clip.fileUrl} controls className="w-full" />
              ) : (
                <ReactPlayer
                  url={clip.fileUrl}
                  width="100%"
                  height="auto"
                  controls
                  config={{ file: { attributes: { controlsList: 'nodownload' } } }}
                />
              )}
            </div>

            <p className="text-[11px] text-white/30 mt-6 text-center">
              Powered by {productName}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
